<template>
  <div class="send-box-container">
    <div class="pt-2 pb-2 d-flex w-100" v-if="hasReplyTo">
      <div class="w-100">
        <i class="fas fa-reply mr-3"></i>
        <el-tooltip content="Click to highlight a message" placement="top" effect="light">
          <a :href="getReplyToHref"
             @click.prevent="scrollToMessage" class="font-weight-bold">
            {{ getSubject }}
          </a>
        </el-tooltip>
        <a href="#" class="ml-3 cursor-pointer" @click.prevent="removeReplyTo">
          <i class="fas fa-times"></i>
        </a>
      </div>
    </div>
    <el-input v-model="subject" class="send-box__subject" placeholder="Subject" ref="subject">
      <template v-slot:append>
        <email-placeholder @select="addPlaceholderToSubject">
          <el-tooltip content="Placeholders" placement="top">
            <span class="color--prpl cursor-pointer"><i class="fas fa-code"></i></span>
          </el-tooltip>
        </email-placeholder>
      </template>
    </el-input>
    <el-select v-model="email_address_id" filterable placeholder="From" class="send-box__subject w-100 mt-1" v-if="!hasEmailAddress">
      <el-option
          v-for="item in getEmailList"
          :key="item.value"
          :label="item.label"
          :value="item.value">
      </el-option>
    </el-select>
    <div class="send-box send-box__editor d-flex mt-1" :style="styles">
      <el-scrollbar
          :native="false"
          class="flex-grow-1"
          wrapClass="no-scroll-x"
      >
        <tinymce-editor
            ref="textarea"
            v-model="body"
            class="content"
        ></tinymce-editor>

      </el-scrollbar>
      <button
          class="send-button"
          @click="send"
          :disabled="!canSend"
      >
        <i class="fas fa-paper-plane"></i>
      </button>
    </div>
    <email-placeholder @select="addPlaceholderToBody">
      <el-tooltip content="Placeholders" placement="top">
        <span class="color--prpl cursor-pointer"><i class="fas fa-code"></i></span>
      </el-tooltip>
    </email-placeholder>
  </div>
</template>

<script>
import InputMixin from "../../../mixins/inputs";
import TinymceEditor from '../../tinymce-editor';
import eventBus from "../../../event-bus";
import EmailPlaceholder from '../../shared/placeholder/email-index.vue';

export default {
  components: {
    TinymceEditor,
    EmailPlaceholder,
  },

  mixins: [InputMixin],

  props: {
    conversation: Object,
    canSendMessage: {
      type: Boolean,
      default: false,
    },
    storeUrl: String,
    styles: Object,
  },

  data() {
    return {
      subject: '',
      body: '',
      email_address_id: null,
      replyTo: null,
    };
  },

  computed: {
    /**
     * @return {Object| null}
     */
    emailConversation() {
      return _.get(this.conversation, 'email_conversation');
    },

    /**
     * @return {Array}
     */
    getEmailList() {
      if (this.emailConversation) {
        return [
          {
            value: _.get(this.emailConversation, 'email_address.id'),
            label: _.get(this.emailConversation, 'email_address.address'),
          },
        ];
      }

      return _.map(_.get(this.conversation, 'outgoing_number.representative.email_addresses'), (item) => {
        return {
          value: _.get(item, 'id'),
          label: _.get(item, 'address'),
        };
      });
    },

    /**
     * @return Boolean
     */
    hasContent() {
      return /\S/i.test(this.content);
    },

    /**
     * @return {boolean}
     */
    canSend() {
      return this.hasContent && !this.inDoNotContact && this.canSendMessage && !_.isEmpty(this.getEmailList);
    },

    /**
     * @return {boolean}
     */
    hasEmailAddress() {
      return !_.isNull(_.get(this.emailConversation, 'email_address.id', null));
    },

    /**
     * @return {boolean}
     */
    hasReplyTo() {
      return ! _.isNull(this.replyTo);
    },

    /**
     * @return {string}
     */
    getReplyToHref() {
      const id = _.get(this.replyTo, 'id');

      return `#${id}`;
    },

    /**
     * @return {string}
     */
    getSubject() {
      return _.get(this.replyTo, 'subject');
    },

    /**
     * @return {Boolean}
     */
    inDoNotContact() {
      return _.get(this.emailConversation, 'do_not_contact', null) !== null;
    },
  },

  methods: {
    /**
     * @return {undefined}
     */
    send() {
      if (!this.hasContent) {
        return;
      }

      if (!this.canSend) {
        return;
      }

      const spinner = this.createSpinner();
      axios.post(this.storeUrl, this.form())
          .then(this.onMessageStored)
          .catch(this.onStoreError)
          .finally(() => {
            this.$nextTick(() => spinner.close());
          });
    },

    /**
     * @return {undefined}
     */
    onMessageStored({data}) {
      this.$emit('outbound-message-created', data.message);
      eventBus.$emit('conversation-list-change', data.conversation);
      this.clearContent();
    },

    /**
     * @return {undefined}
     */
    onStoreError({response}) {
      this.$message({
        message: _.get(response, 'data.message', 'An error occurred.'),
        type: 'error',
        iconClass: 'el-icon-error',
      });
    },

    /**
     * @return {Object}
     */
    form() {
      return {
        conversation_id: _.get(this.conversation, 'id'),
        email_conversation_id: _.get(this.emailConversation, 'id'),
        body: this.body,
        subject: this.subject,
        email_address_id: this.email_address_id,
        reply_to_id: _.get(this.replyTo, 'id'),
      };
    },

    /**
     * @return {Object}
     */
    createSpinner() {
      return this.$loading({
        target: this.$el,
        fullscreen: false,
        spinner: 'el-icon-loading',
      });
    },

    /**
     * @return {undefined}
     */
    clearContent() {
      this.body = '';
      this.subject = '';
      this.replyTo = null;
    },

    /**
     * @param {string} value
     */
    addPlaceholderToSubject(value) {
      this.addTextInCursorPosition(
          _.get(this.$refs, 'subject.$refs.input'),
          _.toString(this.subject),
          value,
          this.subjectPlaceholderSetter
      );
    },

    /**
     * @param {string} value
     */
    subjectPlaceholderSetter(value) {
      this.subject = value;
    },

    /**
     * @param value
     */
    addPlaceholderToBody(value) {
      this.$refs.textarea.editor.execCommand('mceInsertContent', false, value);
    },

    /**
     * @return {undefined}
     */
    scrollToMessage() {
      const id = _.get(this.replyTo, 'id');
      document.getElementById(id).scrollIntoView();
      eventBus.$emit('highlight-email-message', {id: id});
    },

    /**
     * @param {Object} message
     * @return {undefined}
     */
    setReplyTo({message}) {
      this.replyTo = _.get(message, 'email_message');

      const subject = _.get(this.replyTo, 'subject');

      this.subject = `RE: ${subject}`;
    },

    /**
     * @return {undefined}
     */
    removeReplyTo() {
      this.replyTo = null;
    },
  },
}
</script>

<style scoped>

</style>
